import { useCallback, useEffect, useState } from 'react';
import { loadGradeViewerSession } from '../../../lib/gradeHub/session';

export type UseGradeViewerStateOptions = {
  focusIdx: number | null;
  courseId?: string;
  assignmentId?: string;
  onViewerChange?: (slice: { pageIndex: number; zoomPercent: number; fitWidth: boolean }) => void;
};

export function useGradeViewerState({
  focusIdx,
  courseId,
  assignmentId,
  onViewerChange,
}: UseGradeViewerStateOptions) {
  const [zoomPercent, setZoomPercent] = useState(100);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [fitWidth, setFitWidth] = useState(false);
  const [annotateOn, setAnnotateOn] = useState(false);

  useEffect(() => {
    const restored = loadGradeViewerSession(courseId, assignmentId);
    setZoomPercent(restored.zoomPercent);
    setPageIndex(restored.pageIndex);
    setFitWidth(restored.fitWidth);
    setRotationDeg(0);
    setAnnotateOn(false);
  }, [focusIdx, courseId, assignmentId]);

  useEffect(() => {
    onViewerChange?.({ pageIndex, zoomPercent, fitWidth });
  }, [pageIndex, zoomPercent, fitWidth, onViewerChange]);

  const onZoomIn = useCallback(() => setZoomPercent((z) => Math.min(200, z + 10)), []);
  const onZoomOut = useCallback(() => setZoomPercent((z) => Math.max(50, z - 10)), []);
  const onRotate = useCallback(() => setRotationDeg((d) => (d + 90) % 360), []);

  return {
    zoomPercent,
    setZoomPercent,
    rotationDeg,
    pageIndex,
    setPageIndex,
    fitWidth,
    setFitWidth,
    annotateOn,
    setAnnotateOn,
    onZoomIn,
    onZoomOut,
    onRotate,
  };
}
